/**
 * Filename helpers for the document burn-in flow (VE-20837).
 *
 * The ticket spec for the resulting `redacted-document` asset filename
 * (with the version suffix added later for parity with imageRedactionSaga's
 * `${name}[REDACTED].v${version}.jpeg` convention):
 *   - When redactions are present:
 *       `court case 123.pdf` -> `court case 123[REDACTED].v1.pdf`
 *       `court case 123.doc` -> `court case 123.doc[REDACTED].v1.pdf`
 *   - When the user confirmed an empty redaction set:
 *       `court case 123.pdf` -> `court case 123[NO_REDACTIONS].v1.pdf`
 *       `court case 123.doc` -> `court case 123.doc[NO_REDACTIONS].v1.pdf`
 *
 * The behavior is:
 *   - If the source name ends in `.pdf` (case-insensitive), drop the
 *     trailing `.pdf`, append `<tag>.v<version>.pdf`.
 *   - Otherwise keep the original extension intact and append
 *     `<tag>.v<version>.pdf` (the burn-in always produces a PDF
 *     regardless of the source).
 *   - If the source has no extension at all, just append the tag +
 *     version + `.pdf`.
 */

const REDACTED_TAG = '[REDACTED]';
const NO_REDACTIONS_TAG = '[NO_REDACTIONS]';

export function buildRedactedFilename(
  sourceName: string,
  hasRedactions: boolean,
  version: number
): string {
  const tag = hasRedactions ? REDACTED_TAG : NO_REDACTIONS_TAG;
  // Negative / non-integer versions never come from the saga in practice but
  // we still want a sensible filename if a caller passes something odd.
  const safeVersion =
    Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
  const suffix = `${tag}.v${safeVersion}.pdf`;
  const trimmed = sourceName.trim();
  if (!trimmed) {
    return suffix;
  }
  // Case-insensitive .pdf suffix check; preserve the original casing of the
  // base name so `Court Case.PDF` -> `Court Case[REDACTED].v1.pdf` keeps the base.
  if (/\.pdf$/i.test(trimmed)) {
    const base = trimmed.slice(0, -4);
    return `${base}${suffix}`;
  }
  return `${trimmed}${suffix}`;
}
